import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import Iconify from 'src/components/Iconify';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "src/@/components/ui/tabs";
import { formataNumeroCelular, verifyRules } from 'src/utils/functions';
import v2_actionEditarCanal from 'src/actions/v2/cliente/canais/v2_actionEditarCanal';
import DialogNewChannel from './DialogNewChannel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import v2_actionDeletarCanal from 'src/actions/v2/cliente/canais/v2_actionDeletarCanal';
import getActionClienteByClienteId from 'src/actions/clientes/getActionClienteByClienteId';
import actionListaClientesCanais from 'src/actions/clientes/actionListaClientesCanais';
import getSpent from 'src/actions/clientes/getSpent';
import config from 'src/config';
import { CANAIS_COMPONENT_DESIGN as D } from './CanaisComponent.design';

// ----------------------------------------------------------------------

CanaisComponent.propTypes = {
  listaCanais: PropTypes.array,
  canaisError: PropTypes.string,
};

const PLATFORM_UI = {
  whatsapp: {
    label: 'WhatsApp',
    icon: 'logos:whatsapp-icon',
    iconBox: 'bg-green-50',
  },
  whatsapp_business: {
    label: 'WhatsApp Business',
    icon: 'hugeicons:whatsapp-business',
    iconBox: 'bg-emerald-50 text-[#25D366]',
  },
  instagram: {
    label: 'Instagram Direct',
    icon: 'skill-icons:instagram',
    iconBox: 'bg-pink-50',
  },
  messenger: {
    label: 'Messenger',
    icon: 'logos:messenger',
    iconBox: 'bg-blue-50',
  },
  telegram: {
    label: 'Telegram',
    icon: 'logos:telegram',
    iconBox: 'bg-sky-50',
  },
  webchat: {
    label: 'Webchat',
    icon: 'streamline-plump:web',
    iconBox: 'bg-violet-50 text-[#7C3AED]',
  },
};

const getPlatformUi = (platform) => PLATFORM_UI[platform] || {
  label: platform || 'Canal',
  icon: 'solar:chat-round-dots-bold',
  iconBox: 'bg-gray-50 text-gray-500',
};

// Quando a conta social (Contas Sociais) e apagada, o backend so remove o ponteiro
// mkt_account_id — de proposito mantem status_connection_whatsapp: 'conectado' e as
// credenciais em config.meta.*, pro canal continuar enviando/recebendo (ver comentario
// em mkt.accounts.controller.js:deleteAccount). Isso so cobre o caso em que as
// credenciais em si nunca existiram ou foram removidas: canal genuinamente incapaz de
// enviar/receber, mas exibindo "Ativo" por um flag que nunca foi atualizado. Nao cobre
// (de proposito) o caso em que a conta social sumiu mas as credenciais continuam
// validas — esse canal segue funcionando e nao deve ser desconectado.
// hasAccessToken vem calculado do backend (a lista de canais nao recebe o token em si,
// so o booleano de presenca — ver o $project de listaCanaisPorCliente).
function hasValidCredentials(channel) {
  if (channel.platform === 'messenger') {
    return !!(channel.config?.meta?.messenger?.pageId && channel.config?.meta?.messenger?.hasAccessToken);
  }
  if (channel.platform === 'instagram') {
    return !!(channel.config?.meta?.instagram?.instagramUserId && channel.config?.meta?.instagram?.hasAccessToken);
  }
  return true; // whatsapp/telegram/webchat/whatsapp_business conectam por outro mecanismo
}

// A correcao automatica abaixo so pode rodar sobre um payload que realmente traz as
// credenciais. Se a lista vier sem config (endpoint antigo, projecao mudada), todo canal
// Meta pareceria sem credencial e a tela desconectaria canais que funcionam.
const hasCredentialData = (channel) =>
  !['messenger', 'instagram'].includes(channel.platform) || channel.config?.meta !== undefined;

const isChannelConnected = (channel) => channel.status_connection_whatsapp === 'conectado' && hasValidCredentials(channel);

function getChannelDetail(channel) {
  if (channel.platform === 'whatsapp') return formataNumeroCelular(channel.numero_whatsapp);
  if (channel.platform === 'whatsapp_business') {
    return channel.config?.meta?.whatsappBusinness?.wabaId
      ? `WABA ${channel.config.meta.whatsappBusinness.wabaId}`
      : null;
  }
  if (channel.platform === 'messenger') {
    return channel.config?.meta?.messenger?.pageName || channel.config?.meta?.messenger?.name || null;
  }
  if (channel.platform === 'instagram') {
    return channel.config?.meta?.instagram?.username
      ? `@${channel.config.meta.instagram.username}`
      : null;
  }
  if (channel.platform === 'telegram') {
    return channel.config?.telegram?.botUsername
      ? `@${channel.config.telegram.botUsername}`
      : null;
  }
  return null;
}

const SkeletonCard = () => {
  return (
    <div className={D.skeleton.card}>
      <div className="flex items-start gap-3 mb-3">
        <div className={D.skeleton.avatar} />
        <div className="flex-1 min-w-0 pt-0.5">
          <div className={`w-2/3 h-3.5 ${D.skeleton.line}`} />
          <div className={`w-1/2 h-3 ${D.skeleton.line} mt-2`} />
        </div>
        <div className={`w-16 h-5 ${D.skeleton.pill} flex-shrink-0`} />
      </div>
      <div className={`w-24 h-4 ${D.skeleton.line} mb-4`} />
      <div className="flex gap-1.5 pt-3.5 border-t border-[#f0f0f3]">
        <div className={`flex-1 h-9 ${D.skeleton.line}`} />
        <div className={`w-9 h-9 ${D.skeleton.line}`} />
      </div>
    </div>
  );
};

const ErrorCard = ({ message, onRetry }) => {
  const { t } = useTranslation('settings');
  return (
    <div className={D.error.wrap}>
      <div className={D.error.card}>
        <div className={D.error.iconBox}>
          <Iconify icon="solar:danger-circle-bold" width={30} height={30} />
        </div>
        <h3 className={D.error.title}>{t('channels.list.error.title')}</h3>
        <p className={D.error.desc}>{message}</p>
        <button onClick={onRetry} className={D.error.retryBtn}>
          <Iconify icon="solar:restart-bold" width={17} height={17} />
          <span>{t('common:buttons.retry')}</span>
        </button>
      </div>
    </div>
  );
};

export default function CanaisComponent({ user, listaCanais = [], canaisError, onConnect, onCallback }) {
  const { t } = useTranslation('settings');
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams();
  const [openNewChannel, setOpenNewChannel] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [spent, setSpent] = useState(null);
  const [viewMode, setViewMode] = useState('card'); // 'card' | 'table'
  const [canalToDelete, setCanalToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const novoCanalPlatform = searchParams.get('novo');

  useEffect(() => {
    if (!novoCanalPlatform) return;
    setOpenNewChannel(true);
  }, [novoCanalPlatform]);

  const TABLE_HEAD = [
    { id: 'name', label: t('channels.list.table.name'), className: 'flex-1 min-w-[220px]' },
    { id: 'platform', label: t('channels.list.table.platform'), className: 'flex-1 min-w-[220px]' },
    { id: 'status', label: t('common:labels.status'), className: 'w-36' },
    { id: 'actions', label: t('common:buttons.actions'), className: 'w-28 text-right' },
  ];

  async function handleGetSpent() {
    const hoje = new Date()
    const inicio = hoje.toISOString().split('T')[0]
    const final = hoje.toISOString().split('T')[0]
    let querys = `?inicio=${inicio}&final=${final}&periodo=mes_atual`
    const response = await getSpent(user.clienteId, querys)
    setSpent(response?.data)
  }

  async function handleDesconectarCanal(canalId) {
    try {
      let data = {
        scope: {
          status_connection_whatsapp: "desconectado"
        }
      }
      const response = await v2_actionEditarCanal(canalId, data)
      if (response.status === 200) {
        onCallback()
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleRetry = async () => {
    setIsLoading(true)
    setHasError(false)
    setErrorMessage('')
    try {
      await handleGetSpent()
      await onCallback()
    } catch (error) {
      setHasError(true)
      setErrorMessage(t('channels.list.error.loadMessage'))
    } finally {
      setTimeout(() => setIsLoading(false), 1000)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        await handleGetSpent()
        await onCallback()
      } catch (error) {
        setHasError(true)
        setErrorMessage(t('channels.list.error.loadMessage'))
      } finally {
        setTimeout(() => setIsLoading(false), 1000)
      }
    }

    loadData()
  }, [])

  useEffect(() => {
    if (canaisError) {
      setHasError(true)
      setErrorMessage(canaisError)
      setIsLoading(false)
    }
  }, [canaisError])

  // Corrige em segundo plano canais com status_connection_whatsapp: 'conectado' mas sem
  // credenciais reais (ver hasValidCredentials) — nunca conseguiram enviar/receber, so o
  // flag nunca foi atualizado. correctedIdsRef evita reenviar a mesma correcao a cada
  // refetch de listaCanais enquanto o backend nao devolve o status ja corrigido.
  const correctedIdsRef = useRef(new Set());
  useEffect(() => {
    listaCanais.forEach((channel) => {
      const isBroken = channel.status_connection_whatsapp === 'conectado'
        && hasCredentialData(channel)
        && !hasValidCredentials(channel);
      if (isBroken && !correctedIdsRef.current.has(channel._id)) {
        correctedIdsRef.current.add(channel._id);
        handleDesconectarCanal(channel._id);
      }
    });
  }, [listaCanais]) // eslint-disable-line react-hooks/exhaustive-deps

  const channelsUse = spent?.channels?.use;
  const channelsLimit = spent?.channels?.limit;
  const isLimitReached = Number.isFinite(channelsUse) && Number.isFinite(channelsLimit) && channelsUse >= channelsLimit;
  const hasNoPermission = !verifyRules(user.rules, "canal.criar");
  const isBlockedByConfig = !!config.settings.blockChannelUsage;
  const cannotCreateChannel = hasNoPermission || isBlockedByConfig || isLimitReached;
  const blockReason = hasNoPermission
    ? t('channels.list.blockReason.noPermission')
    : isBlockedByConfig
      ? t('channels.list.blockReason.disabled')
      : isLimitReached
        ? t('channels.list.blockReason.limitReached')
        : null;

  const availablePlatforms = useMemo(
    () => [...new Set(listaCanais.map((channel) => channel.platform).filter(Boolean))],
    [listaCanais]
  );

  const filteredChannels = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase('pt-BR');

    return listaCanais.filter((channel) => {
      const platform = getPlatformUi(channel.platform);
      const detail = getChannelDetail(channel);
      const matchesSearch = !normalizedSearch
        || channel.nome?.toLocaleLowerCase('pt-BR').includes(normalizedSearch)
        || platform.label.toLocaleLowerCase('pt-BR').includes(normalizedSearch)
        || detail?.toLocaleLowerCase('pt-BR').includes(normalizedSearch);
      const matchesPlatform = platformFilter === 'all' || channel.platform === platformFilter;
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'connected' ? isChannelConnected(channel) : !isChannelConnected(channel));

      return matchesSearch && matchesPlatform && matchesStatus;
    });
  }, [listaCanais, searchTerm, platformFilter, statusFilter]);

  const hasActiveFilters = searchTerm || platformFilter !== 'all' || statusFilter !== 'all';
  const clearFilters = () => {
    setSearchTerm('');
    setPlatformFilter('all');
    setStatusFilter('all');
  };

  async function renoveAStorageMultisession() {
    const responseCliente = await getActionClienteByClienteId(user.clienteId);
    const response_clientes_canais = await actionListaClientesCanais(user.clienteId);

    let listaNumerosSubs = response_clientes_canais.data.numeros_subs;
    const numeros_subs_reduce = listaNumerosSubs.map((item) => item.nome)

    const newClienteObj = {
      ...responseCliente.data.cliente,
      clientes_canais: responseCliente.data.cliente.multiSession === 1 ? response_clientes_canais.data.data : [],
      numeros_subs: responseCliente.data.cliente.multiSession === 1 ? numeros_subs_reduce : [],
      numeros_subs_data: responseCliente.data.cliente.multiSession === 1 ? listaNumerosSubs : []
    }

    localStorage.setItem('multiSession', responseCliente.data.cliente.multiSession);
    localStorage.setItem('marketplaceId', responseCliente.data.cliente.marketplace_id);
    localStorage.setItem('cliente_data', JSON.stringify(newClienteObj));
  }

  async function handleDeleteCanal(canalId) {
    try {
      const response = await v2_actionDeletarCanal(canalId)
      if (response.status === 200) {
        onCallback();
        renoveAStorageMultisession()
        handleGetSpent()
      }
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <div className={D.root} style={{ fontFamily: D.fontStack }}>
      <div className={D.stickyHeader}>
        <div className={D.container}>
          <div className={D.topbar}>
            <div className={D.topbarIdentity}>
              <div className="min-w-0">
                <h1 className={D.title}>{t('channels.list.header.title')}</h1>
                <p className={D.subtitle}>{t('channels.list.header.subtitle')}</p>
              </div>
            </div>
            <div className={D.topbarActions}>
              {Number.isFinite(channelsLimit) && (
                <div className={D.planUsage}>
                  <span className={D.planUsageText}>{t('channels.list.planUsage', { use: channelsUse || 0, limit: channelsLimit })}</span>
                  <div className={D.planUsageBar}>
                    <div
                      className={`${D.planUsageFill} ${isLimitReached ? D.planUsageFillDanger : D.planUsageFillOk}`}
                      style={{ width: `${Math.min(((channelsUse || 0) / Math.max(channelsLimit, 1)) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
              <button
                onClick={() => setOpenNewChannel(true)}
                disabled={cannotCreateChannel}
                title={blockReason || ''}
                className={`${D.actionButton} ${cannotCreateChannel ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
              >
                <span>{t('channels.list.header.newChannelButton')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <DialogNewChannel
        open={openNewChannel}
        initialPlatform={novoCanalPlatform || ''}
        onClose={() => {
          setOpenNewChannel(false);
          if (novoCanalPlatform) {
            searchParams.delete('novo');
            setSearchParams(searchParams, { replace: true });
          }
        }}
        onSubmit={(value) => {
          setOpenNewChannel(false)
          navigate({
            pathname: '/dashboard/configuracoes/channelDetails',
            search: '?channelId=' + value + '&new=1'
          });
          onCallback()
          renoveAStorageMultisession()
        }}
      />

      <div className={`${D.container} ${D.pageBody}`}>
        <div className={D.filtersRow}>
          <div className={D.searchWrap}>
            <Iconify icon="eva:search-outline" width={18} className={D.searchIcon} />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('channels.list.filters.searchPlaceholder')}
              className={D.search}
            />
          </div>
          <div className={D.filterGroup}>
            <select
              value={platformFilter}
              onChange={(event) => setPlatformFilter(event.target.value)}
              className={D.select}
              aria-label={t('channels.list.filters.platformLabel')}
            >
              <option value="all">{t('channels.list.filters.allPlatforms')}</option>
              {availablePlatforms.map((platform) => (
                <option key={platform} value={platform}>{getPlatformUi(platform).label}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className={D.select}
              aria-label={t('channels.list.filters.statusLabel')}
            >
              <option value="all">{t('channels.list.filters.allStatuses')}</option>
              <option value="connected">{t('channels.list.status.connected')}</option>
              <option value="disconnected">{t('channels.list.status.disconnected')}</option>
            </select>
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList className={D.viewToggleWrap}>
                <TabsTrigger value="card" className={D.viewToggleBtn}>
                  <Iconify icon="mingcute:card-fill" width={15} className="mr-1.5" />
                  {t('channels.list.viewToggle.cards')}
                </TabsTrigger>
                <TabsTrigger value="table" className={D.viewToggleBtn}>
                  <Iconify icon="mingcute:table-2-fill" width={15} className="mr-1.5" />
                  {t('channels.list.viewToggle.table')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <div>
          {isLoading ? (
            viewMode === 'card' ? (
              <div className={D.cards.grid}>
                {[...Array(4)].map((_, index) => <SkeletonCard key={index} />)}
              </div>
            ) : (
              <div className={D.skeleton.table}>
                <div className={D.skeleton.tableHead}></div>
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className={D.skeleton.tableRow}></div>
                  ))}
                </div>
              </div>
            )
          ) : hasError ? (
            <ErrorCard message={errorMessage} onRetry={handleRetry} />
          ) : filteredChannels.length === 0 && (hasActiveFilters || listaCanais.length === 0) ? (
            <EmptyState
              filtered={Boolean(hasActiveFilters)}
              onClear={clearFilters}
              onCreate={() => setOpenNewChannel(true)}
              createDisabled={cannotCreateChannel}
            />
          ) : (
            viewMode === 'card' ? (
                <div className={D.cards.grid}>
                  {filteredChannels.map((item) => (
                    <CanalCard
                      key={item._id}
                      user={user}
                      onDelete={() => handleDeleteCanal(item._id)}
                      botao={() => {
                        if (item.platform === "instagram" || item.platform === "messenger" || item.platform === "whatsapp_business") {
                          onConnect()
                        } else if (item.platform === "telegram") {
                          // A conexão do Telegram é só colar o token do bot — acontece
                          // na própria tela de detalhes, sem QR nem OAuth.
                          navigate({
                            pathname: '/dashboard/configuracoes/channelDetails',
                            search: '?channelId=' + item._id
                          });
                        } else {
                          navigate({
                            pathname: '/dashboard/configuracoes/conectar-whatsapp',
                            search: '?channelId=' + item._id
                          });
                        }
                      }}
                      onConfig={() => {
                        navigate({
                          pathname: '/dashboard/configuracoes/channelDetails',
                          search: '?channelId=' + item._id
                        })
                      }}
                      canal={item}
                      onDesconectar={() => handleDesconectarCanal(item._id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white border border-[#e7e7ec] rounded-[12px] overflow-hidden min-w-0">
                  <div className="overflow-x-auto">
                    <div className="min-w-[800px]">
                      <div className="flex items-center px-4 py-3 border-b border-[#f0f0f3] text-[12px] font-semibold uppercase tracking-[0.04em] text-[#a1a1aa]">
                        {TABLE_HEAD.map((head) => (
                          <div key={head.id} className={head.className || ''}>{head.label}</div>
                        ))}
                      </div>
                      {filteredChannels.map((item) => (
                        <ChannelTableRow
                          key={item._id}
                          channel={item}
                          user={user}
                          onConfigure={() => navigate({
                            pathname: '/dashboard/configuracoes/channelDetails',
                            search: `?channelId=${item._id}`
                          })}
                          onConnect={() => {
                            if (item.platform === 'whatsapp_business') {
                              onConnect();
                            } else if (item.platform === 'whatsapp') {
                              navigate({
                                pathname: '/dashboard/configuracoes/conectar-whatsapp',
                                search: `?channelId=${item._id}`
                              });
                            } else {
                              navigate({
                                pathname: '/dashboard/configuracoes/channelDetails',
                                search: `?channelId=${item._id}`
                              });
                            }
                          }}
                          onDisconnect={() => handleDesconectarCanal(item._id)}
                          onDelete={() => setCanalToDelete(item)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )
          )}
        </div>

        {canalToDelete && (
          <ConfirmationModal
            tone="danger"
            icon="solar:trash-bin-trash-bold-duotone"
            title={t('channels.list.deleteModal.title')}
            description={(
              <Trans
                t={t}
                i18nKey="channels.list.deleteModal.message"
                values={{ nome: canalToDelete.nome }}
                components={{ bold: <strong /> }}
              />
            )}
            confirmLabel={t('common:buttons.delete')}
            onCancel={() => setCanalToDelete(null)}
            onConfirm={() => {
              handleDeleteCanal(canalToDelete._id);
              setCanalToDelete(null);
            }}
          />
        )}
      </div>
    </div>
  );
}


const EmptyState = ({ filtered, onClear, onCreate, createDisabled }) => {
  const { t } = useTranslation('settings');

  return (
    <div className={D.empty.container}>
      <div className={D.empty.iconBox}>
        <Iconify icon={filtered ? 'eva:search-outline' : 'solar:chat-round-dots-bold-duotone'} width={30} />
      </div>
      <h3 className={D.empty.title}>
        {filtered ? t('channels.list.empty.filteredTitle') : t('channels.list.empty.title')}
      </h3>
      <p className={D.empty.desc}>
        {filtered ? t('channels.list.empty.filteredDescription') : t('channels.list.empty.description')}
      </p>
      <button
        type="button"
        onClick={filtered ? onClear : onCreate}
        disabled={!filtered && createDisabled}
        className={`mt-5 ${D.actionButton} disabled:opacity-50 disabled:pointer-events-none`}
      >
        <Iconify icon={filtered ? 'eva:refresh-outline' : 'eva:plus-outline'} width={18} />
        {filtered ? t('channels.list.filters.clear') : t('channels.list.header.newChannelButton')}
      </button>
    </div>
  );
};

function StatusBadge({ connected }) {
  const { t } = useTranslation('settings');
  return (
    <span className={`${D.badge} ${connected ? D.badgeOk : D.badgeWarn}`}>
      <span className={`${D.badgeDot} ${connected ? D.badgeDotOk : D.badgeDotWarn}`} />
      {connected ? t('channels.list.status.connected') : t('channels.list.status.disconnected')}
    </span>
  );
}

function ChannelTableRow({ channel, user, onConfigure, onConnect, onDisconnect, onDelete }) {
  const { t } = useTranslation('settings');
  const platform = getPlatformUi(channel.platform);
  const connected = isChannelConnected(channel);
  const detail = getChannelDetail(channel);
  const canEdit = verifyRules(user.rules, 'editar_canal');
  const canDelete = user.clienteId !== channel.cliente_id && canEdit;

  return (
    <div className={D.table.row}>
      <div className="flex-1 min-w-[220px] pr-4 flex items-center gap-3">
        <div className={`${D.table.iconBox} ${platform.iconBox}`}>
          <Iconify icon={platform.icon} width={21} />
        </div>
        <div className="min-w-0">
          <p className={D.table.name}>{channel.nome}</p>
          <p className={D.table.platform}>{platform.label}</p>
        </div>
      </div>
      <div className={`flex-1 min-w-[220px] pr-4 ${D.table.detail}`}>
        {detail || t('channels.list.table.noDetails')}
      </div>
      <div className="w-36">
        {channel.platform !== 'whatsapp_business' && <StatusBadge connected={connected} />}
      </div>
      <div className="w-28 flex justify-end items-center gap-1">
        {!connected && !['instagram', 'messenger'].includes(channel.platform) && (
          <button
            type="button"
            onClick={onConnect}
            className={D.table.actionBtnPrimary}
            title={t('channels.list.actions.connect')}
          >
            <Iconify icon="solar:link-circle-bold" width={19} />
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={onConfigure}
            className={D.table.actionBtn}
            title={t('channels.list.actions.configure')}
          >
            <Iconify icon="solar:settings-bold" width={19} />
          </button>
        )}
        {channel.platform === 'whatsapp' && connected && (
          <button
            type="button"
            onClick={onDisconnect}
            className={D.table.actionBtnWarn}
            title={t('common:buttons.logout')}
          >
            <Iconify icon="solar:logout-2-bold" width={19} />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className={D.table.actionBtnDanger}
            title={t('common:buttons.delete')}
          >
            <Iconify icon="solar:trash-bin-trash-bold" width={19} />
          </button>
        )}
      </div>
    </div>
  );
}

const ConfirmationModal = ({ tone, icon, title, description, confirmLabel, onCancel, onConfirm }) => {
  const { t } = useTranslation('settings');
  const danger = tone === 'danger';

  return (
    <div className={D.modal.overlay} role="presentation" onMouseDown={onCancel}>
      <div
        className={D.modal.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="channel-confirm-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={`${D.modal.iconBox} ${danger ? D.modal.iconBoxDanger : D.modal.iconBoxWarn}`}>
          <Iconify icon={icon} width={25} />
        </div>
        <div>
          <h3 id="channel-confirm-title" className={D.modal.title}>{title}</h3>
          <p className={D.modal.desc}>{description}</p>
        </div>
        <div className={D.modal.actions}>
          <button
            type="button"
            className={D.modal.cancelBtn}
            onClick={onCancel}
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            type="button"
            className={danger ? D.modal.confirmDangerBtn : D.modal.confirmWarnBtn}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

function CanalCard({ user, canal, botao, onConfig, onDelete, onDesconectar }) {
  const { t } = useTranslation('settings');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const platform = getPlatformUi(canal.platform);
  const detail = getChannelDetail(canal);
  const connected = isChannelConnected(canal);
  const canEdit = verifyRules(user.rules, 'editar_canal');
  const canDelete = user.clienteId !== canal.cliente_id && canEdit;
  const canConnect = !connected && ['whatsapp', 'telegram'].includes(canal.platform);

  const handleDeleteConfirm = () => {
    onDelete();
    setShowDeleteModal(false);
  };

  const handleDisconnectConfirm = () => {
    onDesconectar();
    setShowDisconnectModal(false);
  };

  return (
    <>
      <article className={D.cards.item}>
        <div className={D.cards.header}>
          <div className={`${D.cards.iconBox} ${platform.iconBox}`}>
            <Iconify icon={platform.icon} width={22} />
          </div>
          <div className={D.cards.info}>
            <p className={D.cards.name} title={canal.nome}>{canal.nome}</p>
            <div className={D.cards.cardDetail} title={detail || undefined}>
              <Iconify icon={detail ? 'eva:at-outline' : 'eva:info-outline'} width={13} className="flex-shrink-0" />
              <span className="truncate">{detail || t('channels.list.card.noDetails')}</span>
            </div>
          </div>
          {canal.platform !== 'whatsapp_business' && <StatusBadge connected={connected} />}
        </div>

        <span className={D.cards.platformChip}>
          <Iconify icon={platform.icon} width={11} />
          {platform.label}
        </span>

        <div className={D.cards.cardFooter}>
          {canConnect && (
            <button
              type="button"
              className={D.cards.connectBtn}
              onClick={botao}
            >
              <Iconify icon="solar:link-circle-bold" width={14} />
              {canal.platform === 'telegram'
                ? t('channels.list.actions.connectTelegram')
                : t('channels.list.actions.connectWhatsapp')}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className={`${canConnect ? '' : 'flex-1'} ${D.cards.configBtn}`}
              onClick={onConfig}
            >
              <Iconify icon="solar:settings-bold" width={14} />
              <span>{t('channels.list.actions.configure')}</span>
            </button>
          )}
          {canal.platform === 'whatsapp' && connected && (
            <button
              type="button"
              className={D.cards.disconnectBtn}
              onClick={() => setShowDisconnectModal(true)}
              title={t('common:buttons.logout')}
            >
              <Iconify icon="solar:logout-2-bold" width={16} />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className={D.cards.deleteBtn}
              onClick={() => setShowDeleteModal(true)}
              title={t('channels.list.card.removeChannelTitle')}
            >
              <Iconify icon="solar:trash-bin-trash-bold" width={16} />
            </button>
          )}
        </div>
      </article>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ConfirmationModal
          tone="danger"
          icon="solar:trash-bin-trash-bold-duotone"
          title={t('channels.list.deleteModal.title')}
          description={(
            <Trans
              t={t}
              i18nKey="channels.list.deleteModal.message"
              values={{ nome: canal.nome }}
              components={{ bold: <strong /> }}
            />
          )}
          confirmLabel={t('common:buttons.delete')}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectModal && (
        <ConfirmationModal
          tone="warning"
          icon="solar:logout-2-bold-duotone"
          title={t('channels.list.disconnectModal.title')}
          description={(
            <Trans
              t={t}
              i18nKey="channels.list.disconnectModal.message"
              values={{ nome: canal.nome }}
              components={{ bold: <strong /> }}
            />
          )}
          confirmLabel={t('channels.list.disconnectModal.confirmButton')}
          onCancel={() => setShowDisconnectModal(false)}
          onConfirm={handleDisconnectConfirm}
        />
      )}
    </>
  );
}
